import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MapPin, Star, Phone, Clock, ChevronRight, Search, Calendar, Loader2, Stethoscope, PawPrint, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Species options for display
const SPECIES_EMOJI = {
  'chien': '🐕',
  'chat': '🐱',
  'lapin': '🐰',
  'oiseau': '🐦',
  'rongeur': '🐹',
  'reptile': '🦎',
  'cheval': '🐴',
  'nac': '🦔',
  'autre': '🐾'
};

const RecherchePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [veterinarians, setVeterinarians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState('');
  const [selectedVet, setSelectedVet] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    animalName: '',
    animalType: 'chien',
    reason: ''
  });
  const [isBooking, setIsBooking] = useState(false);
  
  // Sprint 6: Animal selection
  const [patientAnimals, setPatientAnimals] = useState([]);
  const [selectedAnimalId, setSelectedAnimalId] = useState(null);
  const [animalSelectionMode, setAnimalSelectionMode] = useState('existing'); // existing or new

  // Fetch approved veterinarians
  const fetchVeterinarians = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/veterinarians`);
      if (response.ok) {
        const data = await response.json();
        // Filter only verified vets (is_active can be null/undefined which is treated as active)
        const verifiedVets = data.filter(vet => vet.is_verified && vet.is_active !== false);
        setVeterinarians(verifiedVets);
      }
    } catch (error) {
      console.error('Error fetching vets:', error);
      toast.error('Erreur lors du chargement des vétérinaires');
    }
    setLoading(false);
  }, []);

  const fetchPatientAnimals = useCallback(async () => {
    const token = localStorage.getItem('patientToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/animals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPatientAnimals(data);
        // Auto-select first animal if available
        if (data.length > 0) {
          setSelectedAnimalId(data[0].animal_id);
          setAnimalSelectionMode('existing');
        } else {
          setAnimalSelectionMode('new');
        }
      }
    } catch (error) {
      console.error('Error fetching patient animals:', error);
    }
  }, []);

  // Refetch data when navigating to this page
  useEffect(() => {
    fetchVeterinarians();
    fetchPatientAnimals();
  }, [fetchVeterinarians, fetchPatientAnimals]);

  // Filter by city
  const filteredVets = veterinarians.filter(vet => {
    if (!searchCity) return true;
    return vet.city?.toLowerCase().includes(searchCity.toLowerCase()) ||
           vet.clinic_name?.toLowerCase().includes(searchCity.toLowerCase());
  });

  // Fetch available slots when date is selected
  const fetchAvailableSlots = async (vetId, date) => {
    setLoadingSlots(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/vet/${vetId}/slots?date=${date}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du chargement des créneaux');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  };

  const handleSelectVet = (vet) => {
    setSelectedVet(vet);
    setSelectedDate('');
    setAvailableSlots([]);
    setSelectedSlot(null);
    setIsBookingModalOpen(true);
    // Reset animal selection
    if (patientAnimals.length > 0) {
      setSelectedAnimalId(patientAnimals[0].animal_id);
      setAnimalSelectionMode('existing');
    } else {
      setAnimalSelectionMode('new');
      setSelectedAnimalId(null);
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedSlot(null);
    if (date && selectedVet) {
      fetchAvailableSlots(selectedVet.vet_id, date);
    }
  };

  const handleBookAppointment = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('patientToken');
    if (!token) {
      toast.error('Veuillez vous connecter pour prendre rendez-vous');
      navigate('/espace-patient');
      return;
    }

    // Validate based on selection mode
    if (animalSelectionMode === 'existing' && !selectedAnimalId) {
      toast.error('Veuillez sélectionner un animal');
      return;
    }
    if (animalSelectionMode === 'new' && !bookingForm.animalName) {
      toast.error('Veuillez renseigner le nom de l\'animal');
      return;
    }
    if (!selectedSlot) {
      toast.error('Veuillez sélectionner un créneau');
      return;
    }

    // Get selected animal info
    const selectedAnimal = patientAnimals.find(a => a.animal_id === selectedAnimalId);
    
    setIsBooking(true);
    try {
      const appointmentData = {
        vet_id: selectedVet.vet_id,
        appointment_type: 'consultation',
        date: selectedDate,
        time: selectedSlot,
        reason: bookingForm.reason
      };
      
      // Add animal info based on selection mode
      if (animalSelectionMode === 'existing' && selectedAnimal) {
        appointmentData.animal_id = selectedAnimalId;
        appointmentData.animal_name = selectedAnimal.name;
        appointmentData.animal_type = selectedAnimal.species;
      } else {
        appointmentData.animal_name = bookingForm.animalName;
        appointmentData.animal_type = bookingForm.animalType;
      }

      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(appointmentData)
      });

      if (response.ok) {
        toast.success('Rendez-vous demandé avec succès !');
        setIsBookingModalOpen(false);
        setBookingForm({ animalName: '', animalType: 'chien', reason: '' });
        setSelectedSlot(null);
        navigate('/espace-patient');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la réservation');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    }
    setIsBooking(false);
  };

  // Get min date (today)
  const minDate = new Date().toISOString().split('T')[0];
  // Get max date (2 months from now)
  const maxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 text-white mb-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">
              Trouvez un vétérinaire près de chez vous
            </h1>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Rechercher par ville ou nom de clinique..."
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="pl-10 bg-white text-gray-800"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              {filteredVets.length} vétérinaire{filteredVets.length > 1 ? 's' : ''} disponible{filteredVets.length > 1 ? 's' : ''}
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
              <p className="text-gray-500">Chargement des vétérinaires...</p>
            </div>
          ) : filteredVets.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVets.map((vet) => (
                <div key={vet.vet_id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Stethoscope className="w-7 h-7 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">{vet.clinic_name}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-4 h-4" />
                          {vet.city}
                        </p>
                        {vet.phone && (
                          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                            <Phone className="w-4 h-4" />
                            {vet.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    {vet.specialties && vet.specialties.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {vet.specialties.slice(0, 3).map((spec, idx) => (
                          <span key={idx} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-medium">{vet.rating?.toFixed(1) || '5.0'}</span>
                        <span className="text-xs text-gray-400">({vet.review_count || 0} avis)</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        vet.accepts_new_patients !== false 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {vet.accepts_new_patients !== false ? 'Accepte nouveaux patients' : 'Complet'}
                      </span>
                    </div>
                  </div>

                  <div className="px-6 pb-6">
                    <Button 
                      onClick={() => handleSelectVet(vet)}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                      disabled={vet.accepts_new_patients === false}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Prendre rendez-vous
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucun vétérinaire trouvé</h3>
              <p className="text-gray-500">
                {searchCity 
                  ? `Aucun résultat pour "${searchCity}". Essayez une autre recherche.`
                  : 'Aucun vétérinaire disponible pour le moment.'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={setIsBookingModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              Prendre rendez-vous
            </DialogTitle>
          </DialogHeader>

          {selectedVet && (
            <div className="space-y-6">
              {/* Vet Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800">{selectedVet.clinic_name}</h3>
                <p className="text-sm text-gray-500">{selectedVet.address}, {selectedVet.city}</p>
              </div>

              {/* Date Selection */}
              <div>
                <Label>Choisir une date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={minDate}
                  max={maxDate}
                  className="mt-1"
                />
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <Label className="mb-2 block">Créneaux disponibles</Label>
                  {loadingSlots ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-green-500 mx-auto" />
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                      {availableSlots.filter(s => s.available).map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setSelectedSlot(slot.time)}
                          className={`py-2 px-3 text-sm rounded-lg border transition-colors ${
                            selectedSlot === slot.time
                              ? 'bg-green-500 text-white border-green-500'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-green-300'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">
                      Aucun créneau disponible pour cette date
                    </p>
                  )}
                </div>
              )}

              {/* Animal Selection - Sprint 6 */}
              {selectedSlot && (
                <div className="space-y-4 border-t pt-4">
                  <Label className="text-base font-semibold">Sélectionner un animal</Label>
                  
                  {/* Selection Mode Toggle */}
                  {patientAnimals.length > 0 && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setAnimalSelectionMode('existing')}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors text-sm font-medium ${
                          animalSelectionMode === 'existing'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <PawPrint className="w-4 h-4 inline mr-2" />
                        Mes animaux
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnimalSelectionMode('new')}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors text-sm font-medium ${
                          animalSelectionMode === 'new'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        Nouvel animal
                      </button>
                    </div>
                  )}
                  
                  {/* Existing Animals List */}
                  {animalSelectionMode === 'existing' && patientAnimals.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {patientAnimals.map((animal) => (
                        <button
                          key={animal.animal_id}
                          type="button"
                          onClick={() => setSelectedAnimalId(animal.animal_id)}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            selectedAnimalId === animal.animal_id
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`animal-select-${animal.animal_id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">
                              {SPECIES_EMOJI[animal.species] || '🐾'}
                            </span>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 truncate">{animal.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {animal.breed || animal.species}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* New Animal Form */}
                  {animalSelectionMode === 'new' && (
                    <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                      <div>
                        <Label>Nom de l'animal *</Label>
                        <Input
                          type="text"
                          placeholder="Ex: Max, Luna..."
                          value={bookingForm.animalName}
                          onChange={(e) => setBookingForm({...bookingForm, animalName: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Type d'animal *</Label>
                        <select
                          value={bookingForm.animalType}
                          onChange={(e) => setBookingForm({...bookingForm, animalType: e.target.value})}
                          className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <option value="chien">🐕 Chien</option>
                          <option value="chat">🐱 Chat</option>
                          <option value="lapin">🐰 Lapin</option>
                          <option value="oiseau">🐦 Oiseau</option>
                          <option value="rongeur">🐹 Rongeur</option>
                          <option value="nac">🦔 NAC</option>
                          <option value="autre">🐾 Autre</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-500">
                        💡 Cet animal sera ajouté à votre carnet de santé après le RDV
                      </p>
                    </div>
                  )}
                  
                  {/* Reason */}
                  <div>
                    <Label>Motif de la consultation</Label>
                    <Input
                      type="text"
                      placeholder="Ex: Vaccination, bilan de santé..."
                      value={bookingForm.reason}
                      onChange={(e) => setBookingForm({...bookingForm, reason: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Confirm Button */}
              {selectedSlot && (
                <Button 
                  onClick={handleBookAppointment}
                  disabled={isBooking || (animalSelectionMode === 'existing' && !selectedAnimalId) || (animalSelectionMode === 'new' && !bookingForm.animalName)}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                  data-testid="confirm-booking-btn"
                >
                  {isBooking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Réservation en cours...
                    </>
                  ) : (
                    <>
                      Confirmer le rendez-vous
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default RecherchePage;
